"use client";

import * as React from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";

import { cn } from "../../lib/utils";

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    alwaysShowScrollbar?: boolean;
    scrollBarClassName?: string;
  }
>(
  (
    { className, children, alwaysShowScrollbar, scrollBarClassName, ...props },
    ref
  ) => (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn("relative overflow-hidden", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        onScroll={props.onScroll}
        className="h-full w-full rounded-[inherit] [&>div]:min-h-full [&>div]:!flex [&>div>div]:flex-1"
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar
        forceMount={alwaysShowScrollbar ? true : undefined}
        className={cn(
          alwaysShowScrollbar && "data-[state=hidden]:opacity-100",
          scrollBarClassName
        )}
      />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  )
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none transition-colors z-40",
      orientation === "vertical" &&
        "h-full w-1.5 border-l border-l-transparent p-0",
      orientation === "horizontal" &&
        "h-1.5 flex-col border-t border-t-transparent p-0",
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
